import { type UseResizeObserverCallback, useRafCallback, useResizeObserver } from "@react-hookz/web"
import type { Row as TanStackRow, RowData as TanStackRowData } from "@tanstack/react-table"
import { useState } from "react"

import { cn } from "../../lib/utils"
import { TableCell, TableRow } from "../ui/table"
import { useDataTableCtx } from "./DataTable"

type DataTableRowExpandedProps<TRowData extends TanStackRowData> = {
  row: TanStackRow<TRowData>
}
export const DataTableRowExpanded = <TRowData extends TanStackRowData>({
  row,
}: DataTableRowExpandedProps<TRowData>) => {
  const { columns, elements } = useDataTableCtx<TRowData>()

  const rowSelectionColumn = columns.find(({ id }) => id === "__select__")
  const isRowSelectionEnabled = Boolean(rowSelectionColumn)

  const placeholder = isRowSelectionEnabled ? (
    <TableCell className="z-[12] sticky left-0 !shadow-[inset_0px_-1px_0px_0px_var(--wb-color-neutral-0)] !bg-neutral-0" />
  ) : undefined

  if (row.getIsExpanded()) {
    return (
      <TableRow className="hover:!bg-root-background-base">
        {placeholder}
        <TableCell
          colSpan={columns.length - (isRowSelectionEnabled ? 1 : 0)}
          className="!bg-neutral-0"
        >
          <ExpandedStickyRow selectedColumnSize={rowSelectionColumn?.size}>
            {elements.expandedRow}
          </ExpandedStickyRow>
        </TableCell>
      </TableRow>
    )
  }

  return null
}

function ExpandedStickyRow<TRowData extends TanStackRowData>({
  children,
  selectedColumnSize = 0,
}: {
  children: React.ReactNode
  isSticky?: boolean
  selectedColumnSize?: number
}) {
  const [maxWidth, setMaxWidth] = useState<number | null>(null)
  const { config, wrapperRef } = useDataTableCtx<TRowData>()

  const [observerHandler] = useRafCallback<UseResizeObserverCallback>((entry) => {
    setMaxWidth(entry.contentRect.width)
  })

  useResizeObserver(wrapperRef, observerHandler, config.isExpandedRowSticky)

  if (config.isExpandedRowSticky) {
    return (
      <div
        className={cn("sticky")}
        style={{
          left: selectedColumnSize + 4, // 4px is the padding of the table cell
          maxWidth: maxWidth
            ? `${maxWidth - selectedColumnSize - 10}px` // is the sum of the selected column size and the padding of the table cell
            : undefined,
        }}
      >
        {children}
      </div>
    )
  }

  return <>{children}</>
}
